// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_PACKAGE,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { IPackage } from '~/utils/interface/package';
import { useAppDispatch } from '~/utils/redux/hooks';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import { deletePackage, getListPackage } from '~/thunks/package/packageThunk';
import { packageActions, selectIsRefreshListPackage } from '~/thunks/package/packageSlice';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { PACKAGE_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './Package.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsPackage = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onClone: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IPackage>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'image',
      label: t('table_header_title_image'),
      render: (record: IPackage) => (
        <Avatar
          src={record.packageImage}
          alt={t('common_alt_img', { ns: 'common/base' })}
          variant='rounded'
          className={cx('image')}
        />
      ),
    },
    {
      id: 'title',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title')}</Box>
          <SortComponent name='title' sortType={getSortType('title')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPackage) => <Box className={cx('textValue')}>{record?.title}</Box>,
    },
    {
      id: 'rentalPrice',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_rental_price')}</Box>
          <SortComponent name='rental price' sortType={getSortType('rental price')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPackage) => (
        <Box className={cx('textValue')}>{formatCurrencyUSD({ value: record?.rentalPrice, showDecimal: true })}</Box>
      ),
      minWidth: 150,
    },
    {
      id: 'description',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_short_description')}</Box>
          <SortComponent name='description' sortType={getSortType('description')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPackage) => <Box className={cx('textValue')}>{record?.description || EMPTY_VALUE}</Box>,
    },
    {
      id: 'note',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_note')}</Box>
          <SortComponent name='note' sortType={getSortType('note')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPackage) => <Box className={cx('textValue')}>{record?.note || EMPTY_VALUE}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IPackage) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
          {
            label: t('table_action_clone', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.commonIconClone}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onClone(record.id),
          },
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Package = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreatePackages, canViewPackages, canEditPackages, canDeletePackages } =
    usePermissions(PACKAGE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/packages', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListPackage = useSelector(selectIsRefreshListPackage);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_PACKAGE);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [packageList, setPackageList] = useState<IPackage[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [idPackage, setIdPackage] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListPackage(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListPackage) return;

    handleGetListPackage(paramObject);

    dispatch(packageActions.setRefreshPackageList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListPackage]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListPackage = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getListPackage(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setPackageList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createPackage);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createPackage);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createPackage);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createPackage);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createPackage);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createPackage);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createPackage);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createPackage);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createPackage);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updatePackage(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updatePackage(id));
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdPackage(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdPackage(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deletePackage(idPackage))
      .unwrap()
      .then(() => {
        setIdPackage(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(packageActions.setRefreshPackageList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleClone = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.clonePackage(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.clonePackage(id));
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='packagePage' className={cx('container')}>
      <BaseToolbar
        title={t('package_title_package_management')}
        // total={t('package_title_total_package', { total: totalItems })}
      >
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('package_title_search')}
        />

        {canCreatePackages && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('package_title_add_package')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewPackages ? packageList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsPackage(
            t,
            sortBy,
            handleSort,
            handleEdit,
            handleDelete,
            handleClone,
            canEditPackages,
            canDeletePackages
          )}
          onPageChange={handlePageChange}
          {...(canEditPackages ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default Package;
